import React from "react";
import { Helmet } from "react-helmet";

import DCDialLogoBig from "icons/DCDialLogoBig";
import { MaintenanceIcon } from "icons/warning/MaintenanceIcon";
import { InfoCircleIcon } from "icons/warning/InfoCircleIcon";
import Button from "components/form/Button";
import Helment from "components/layout/helmet";
import { useAppSelector } from "store/hooks";
import { RootState } from "store/store";
import { PortalMode } from "utils/portalMode";

type MaintenanceProps = {
  mode?: Exclude<PortalMode, "live">;
  isChecking?: boolean;
  onCheckAgain?: () => void;
};

const CONTENT = {
  maintenance: {
    pageTitle: "Under Maintenance | Customer Portal",
    title: "We'll be back soon",
    description:
      "The customer portal is temporarily unavailable while we perform maintenance. We are sorry for the inconvenience, please check back shortly.",
    buttonText: "Check again",
  },
  error: {
    pageTitle: "Temporarily Unavailable | Customer Portal",
    title: "Something went wrong",
    description:
      "We are having trouble loading the customer portal right now. Please try again in a few minutes.",
    buttonText: "Try again",
  },
};

/* ------------------------------------- MAINTENANCE ------------------------------------- */

const Maintenance = ({
  mode = "maintenance",
  isChecking = false,
  onCheckAgain,
}: MaintenanceProps) => {
  const { accentColor } = useAppSelector((state: RootState) => state.dashboard);
  const content = CONTENT[mode];

  return (
    <>
      <Helment />
      <Helmet>
        <title>{content.pageTitle}</title>
      </Helmet>
      <div
        data-testid={`portal-${mode}-page`}
        className="portal-shell h-[var(--shell-vh)] w-[var(--shell-vw)] bg-bgPrimary flex items-center justify-center overflow-y-auto animate__animated animate__fadeIn"
      >
        <div className="max-w-[684px] w-full flex flex-col lg:gap-[60px] gap-10 p-5">
          <div className="flex flex-col items-center">
            <div className="lg:w-fit w-36 flex">
              <DCDialLogoBig />
            </div>
            <div
              className={`lg:mt-[60px] mt-10 lg:h-[120px] lg:w-[120px] h-24 w-24 rounded-full flex items-center justify-center ${
                mode === "error" ? "bg-lightYellow" : "bg-bgSecondary"
              }`}
            >
              {mode === "error" ? (
                <InfoCircleIcon
                  aria-hidden="true"
                  className="lg:h-14 lg:w-14 h-11 w-11"
                />
              ) : (
                <MaintenanceIcon
                  aria-hidden="true"
                  strokeColor={accentColor}
                  className="lg:h-14 lg:w-14 h-11 w-11"
                />
              )}
            </div>
            <h1 className="lg:text-[40px] text-[28px] font-bold text-iconObscure pt-[30px] text-center leading-tight">
              {content.title}
            </h1>
            <p className="pt-4 text-center text-txtParagraph lg:text-[22px] text-lg leading-normal">
              {content.description}
            </p>
          </div>
          <Button
            text={content.buttonText}
            isLoading={isChecking}
            onClick={onCheckAgain}
          />
        </div>
      </div>
    </>
  );
};

export default Maintenance;
